import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { exportSectionsToPDF } from '../utils/pdfExport';
import {
  A4_RATIO,
  FOOTER_RESERVE,
  PAGE_BUDGET,
  H,
  ledeHeight,
  readHeight,
  footnoteHeight,
  ruleHeight,
  paginateBlocks,
  paginateRows,
} from './sales_eom.pages';
import './sales_eom.css';

/**
 * SALES — End of Month Report (v7 layout).
 *
 * A cover, then seven numbered sections. Long tables run onto "(CONTINUED)" pages, so the
 * page count follows the month's data (sales_eom.pages.js decides the breaks). Every
 * heading, column header, box label and Owner Action Rule on this page is fixed text; every
 * NUMBER and every SENTENCE comes from the API payload. Nothing here is hand-entered and
 * nothing here is computed from the ledger — the only arithmetic this file performs is bar
 * widths (a value divided by the largest value in its own group), page breaking and display
 * rounding.
 *
 * That division of labour is the point. The four headline boxes, the channel ledger, the
 * category totals and the quadrant tables are all sums of the SAME settled figures on the
 * server, so a table on page 7 cannot quietly disagree with a box on page 2. A renderer
 * that re-added its own totals is exactly how those two used to drift apart. Where two
 * pages genuinely print different populations, the server says so (`ties`) and the page
 * does not claim they match.
 *
 * Uncosted items (no resolvable recipe cost) carry their revenue and quantity everywhere
 * money is counted, and render "—" in the COGS, Profit / Plate, Gross Profit and Margin
 * columns. They are never booked at zero cost, which would manufacture 100%-margin dishes
 * out of missing data.
 */

// ---------------------------------------------------------------------------
// Display formatting. Indian digit grouping, whole rupees, one-decimal percent.
// ---------------------------------------------------------------------------
function inr(value) {
  if (value === undefined || value === null || isNaN(value)) return '₹0';
  const n = Number(value);
  const text = `₹${Math.abs(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  return n <= -0.5 ? `−${text}` : text;
}

/** Uncosted figures arrive as null and must read as "not known", never as zero. */
function inrOrGap(value) {
  return value === null || value === undefined ? '—' : inr(value);
}

function pct(value, digits = 1) {
  if (value === null || value === undefined || isNaN(value)) return '—';
  return `${Number(value).toFixed(digits)}%`;
}

function qty(value) {
  if (value === null || value === undefined || isNaN(value)) return '0';
  return Number(value).toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

/** Bar width as a percentage of the largest value in its own group. Never NaN, never 0-wide. */
const widthOf = (value, max) =>
  `${Math.max(max > 0 ? (Math.max(Number(value) || 0, 0) / max) * 100 : 0, 0.6)}%`;

const joinRead = (read) => (Array.isArray(read) ? read.join(' ') : read || '');

const sentenceCase = (text) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : '');

const COUNT_WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];

/**
 * A dish name, marked † when the dish was priced on some days only: its profit then covers
 * those days, and the footnote says so. Without the mark, Sales − COGS on that row would
 * not equal its Gross Profit and nothing would say why.
 */
function DishName({ item }) {
  return (
    <>
      {item.item_name}
      {item.partially_costed && (
        <sup className="seom-mark" title="Priced on some days only; profit covers those days.">
          †
        </sup>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Fixed page furniture
// ---------------------------------------------------------------------------
function Footer({ page, pages }) {
  return (
    <div className="seom-footer">
      Invexis Analytics · Sales End-of-Month Report <span>|</span> Page {page} of {pages}
    </div>
  );
}

/**
 * The uncosted-items footnote. Rendered at the foot of section 01, section 04 and the last
 * page of the biggest categories — the pages carrying margin figures a costing gap would
 * distort — and absent entirely when every item resolved to a cost.
 */
function Footnote({ footnote }) {
  if (!footnote || !footnote.show || !footnote.text) return null;
  return <p className="seom-footnote">{footnote.text}</p>;
}

function SectionPage({ section, title, lede, continued, page, pages, testid, flow, flowPage, children }) {
  return (
    <section
      className="seom-page"
      data-seom-section={testid}
      data-seom-flow={flow}
      data-seom-flow-page={flow ? flowPage : undefined}
    >
      <p className="seom-eyebrow">{section}</p>
      <h2>
        {title}
        {continued && <small className="seom-continued"> (CONTINUED)</small>}
      </h2>
      {lede && !continued && <p className="seom-lede">{lede}</p>}
      {children}
      <Footer page={page} pages={pages} />
    </section>
  );
}

function ReadBox({ heading, read, quiet = false }) {
  const text = joinRead(read);
  if (!text) return null;
  return (
    <div className={`seom-read${quiet ? ' seom-read-quiet' : ''}`}>
      <h3>{heading}</h3>
      <p>{text}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 01 — SALES SUMMARY
// ---------------------------------------------------------------------------
function SummaryBox({ label, value, caption }) {
  return (
    <div className="seom-box">
      <span className="seom-box-label">{label}</span>
      <strong className="seom-box-value">{inr(value)}</strong>
      <p className="seom-box-caption">{caption}</p>
    </div>
  );
}

function SalesSummary({ summary, footnote, narratives, ties, page, pages }) {
  // The headline block is computed by the same function that produces the Sales Report
  // card, against this report's window, so these four boxes and that card cannot disagree.
  // `summary.global_gross_revenue` is NOT the headline — it is this report's internal
  // all-channel, gross-of-discount ledger, which the channel table on section 04 ties to.
  // Rendering it here is what made the month-end page show takings the app never showed.
  const headline = summary.headline || {};
  const detail = summary.headline_detail || null;
  const byChannel = summary.sales_by_channel || [];
  const maxChannel = Math.max(...byChannel.map((c) => Number(c.gross) || 0), 0);
  const hasOnline = Number(headline.online_gross_revenue) > 0;
  const nothingGivenAway = !(Number(summary.total_brand_deductions) > 0);
  const tiesEverywhere = !!(ties && ties.channels_to_summary && ties.categories_to_summary);

  // A business with its own Gross Profit formula: the boxes are every channel, and Gross
  // Profit is exactly TOTAL SALES − TOTAL COGS − DISCOUNTS as printed.
  const ownFormula = headline.formula === 'sales_cogs_discount';
  const inHouseSales = ownFormula && headline.in_house ? Number(headline.in_house.total_sales) || 0 : 0;
  const salesCaption = ownFormula
    ? `Dine-in ${inr(inHouseSales)} and delivery platforms ${inr(headline.online_gross_revenue)}, before platform discounts.`
    : detail
      ? `In-house takings for the month: ${qty(detail.in_house_plates)} plates across ${qty(
          detail.in_house_dishes
        )} dishes.`
      : 'In-house takings for the month.';
  // "Sales less discounts, commissions…" only where it cannot be misread: the TOTAL SALES
  // box is already net of discount, so with a discount on the page the box arithmetic
  // would appear to take it off twice.
  const wasted = Number(headline.wastage) > 0.005;
  const profitBasis = ownFormula
    ? 'Total sales less total COGS less discounts.'
    : nothingGivenAway && !hasOnline
      ? `Sales less discounts, commissions${wasted ? ', ingredient cost and wastage' : ' and ingredient cost'}.`
      : `In-house takings less ingredient cost${wasted ? ' and wastage' : ''}.`;
  const profitCaption =
    detail && detail.gross_margin_pct !== null && detail.gross_margin_pct !== undefined
      ? `${profitBasis} A ${pct(detail.gross_margin_pct)} gross margin.`
      : profitBasis;

  return (
    <SectionPage
      section="01 — SALES SUMMARY"
      title="MONTHLY SALES SUMMARY"
      lede={`The four headline sales numbers for the month: what you sold, what it cost, what you gave away, and what you kept. ${
        tiesEverywhere
          ? 'Every other page of this report adds up to these figures.'
          : 'These are the same figures as the Sales Report in the app.'
      }`}
      page={page}
      pages={pages}
      testid="summary"
    >
      <div className="seom-boxes">
        <SummaryBox label="TOTAL SALES" value={headline.total_sales} caption={salesCaption} />
        <SummaryBox
          label="TOTAL COGS"
          value={headline.total_cogs}
          caption={ownFormula
            ? 'Recipe cost × plates sold, dine-in and delivery platforms.'
            : 'Cost of the ingredients behind those sales: recipe cost × plates sold.'}
        />
        <SummaryBox
          label="DISCOUNTS & COMMISSIONS"
          value={ownFormula ? headline.total_discount : summary.total_brand_deductions}
          caption={ownFormula
            ? 'Taken by the delivery platforms: discounts and commissions.'
            : 'Given up to menu discounts and delivery platforms.'}
        />
        <SummaryBox label="GROSS PROFIT" value={headline.gross_profit} caption={profitCaption} />
      </div>

      {headline.window_matches_app === false && (
        <p className="seom-note" data-testid="summary-window-note">
          This period was closed by a stock count taken part-way through the month, so this
          report covers only the days up to that count. The Sales Report in the app covers
          the whole calendar month, and will therefore show a larger figure.
        </p>
      )}

      {hasOnline && !ownFormula && (
        <p className="seom-note" data-testid="summary-online-note">
          Delivery-platform orders are reported separately and are not included in TOTAL
          SALES above: {inr(headline.online_gross_revenue)} came through Zomato, Swiggy and
          other aggregators this month. Section 04 breaks that down channel by channel.
        </p>
      )}

      <div className="seom-chart">
        <h3>Sales by Channel</h3>
        {byChannel.length === 0 && <p className="seom-empty">No sales recorded for this period.</p>}
        {byChannel.map((c, index) => (
          <div className="seom-bar-row" key={c.key}>
            <span>{c.label}</span>
            <div>
              <i
                className={`seom-pattern seom-pattern-${index % 5}`}
                style={{ width: widthOf(c.gross, maxChannel) }}
              />
            </div>
            <strong>{inr(c.gross)}</strong>
          </div>
        ))}
      </div>

      <ReadBox heading="OWNER READ" read={narratives && narratives.owner_read} />

      <Footnote footnote={footnote} />
    </SectionPage>
  );
}

// ---------------------------------------------------------------------------
// 02 — PERFORMANCE HIGHLIGHTS
// ---------------------------------------------------------------------------
// The caption under each bar is the Stock Report's line for the dish — plates sold, the
// sales they were billed at, the margin — so the two screens can be read side by side.
const hasDishLine = (item) => item && Number(item.total_sold) > 0;

function TopProfit({ topProfit, narratives, page, pages }) {
  const items = (topProfit && topProfit.items) || [];
  const max = Math.max(...items.map((i) => Number(i.gross_profit) || 0), 0);
  const showDishLine = items.some(hasDishLine);

  return (
    <SectionPage
      section="02 — PERFORMANCE HIGHLIGHTS"
      title="TOP PROFIT CONTRIBUTORS"
      lede={`The ten dishes contributing the most gross profit from in-house sales this month, figured exactly as the Stock Report figures them. Together they make ${pct(
        (topProfit && topProfit.anchor_pct) || 0,
        0
      )} of all dish gross profit.`}
      page={page}
      pages={pages}
      testid="top-profit"
    >
      <div className="seom-chart">
        <h3>Top 10 Dishes by Gross Profit</h3>
        {items.length === 0 && (
          <p className="seom-empty">No item could be costed this period, so no profit ranking is available.</p>
        )}
        {items.map((item, index) => (
          <div className="seom-bar-row seom-bar-ranked" key={item.menu_item_id}>
            <span>
              <b>{item.rank}.</b> {item.item_name}
            </span>
            <div>
              <i
                className={`seom-pattern seom-pattern-${index % 5}`}
                style={{ width: widthOf(item.gross_profit, max) }}
              />
            </div>
            <strong>
              {inr(item.gross_profit)}
              {hasDishLine(item) && (
                <small className="seom-bar-sub">
                  {qty(item.total_sold)} sold · {inr(item.sales_revenue)} · {pct(item.margin_percentage)}
                </small>
              )}
            </strong>
          </div>
        ))}
        {showDishLine && (
          <p className="seom-chart-note">
            Under each figure: plates sold · sales · margin, as the Stock Report shows the dish.
          </p>
        )}
      </div>

      <ReadBox heading="READING THIS CHART" read={narratives && narratives.reading_chart} />
      <ReadBox heading="HIDDEN GEMS" read={narratives && narratives.hidden_gems} />
    </SectionPage>
  );
}

// ---------------------------------------------------------------------------
// 03 — MENU INSIGHT MATRIX
// ---------------------------------------------------------------------------
/**
 * Owner Action Rules are FIXED text — the same advice every month, so the owner learns what
 * a quadrant means once instead of re-reading a fresh paraphrase each period. Only the rows
 * beneath them change.
 *
 * The axis labels say MARGIN because that is what the engine splits on (its healthy margin
 * line); the section intro names the line it applied.
 */
const QUADRANTS = {
  stars: {
    title: '1 · THE STARS',
    axis: 'HIGH VOLUME · HIGH MARGIN',
    rule: 'Anchor dishes. Hold quality, never let them run out, and feature them first on the QR menu. They can carry a small price rise.',
  },
  workhorses: {
    title: '2 · THE WORKHORSES',
    axis: 'HIGH VOLUME · LOW MARGIN',
    rule: 'Popular, but each sale keeps less than your healthy margin. Tighten portions, renegotiate the costliest ingredient, or add ₹10–20 to the price.',
  },
  puzzles: {
    title: '3 · THE PUZZLES',
    axis: 'LOW VOLUME · HIGH MARGIN',
    rule: 'Each plate earns well, but few are sold. Move them up the QR menu, have staff recommend them, and pair them in combos.',
  },
  underperformers: {
    title: '4 · THE UNDERPERFORMERS',
    axis: 'LOW VOLUME · LOW MARGIN',
    rule: 'Few plates and thin profit. Re-cost the recipe or re-price; if a dish stays here two months running, take it off the menu.',
  },
  loss_makers: {
    title: 'SOLD AT OR BELOW COST',
    axis: 'EVERY PLATE LOSES MONEY',
    rule: 'Each of these costs more to make than it sells for, so every extra plate deepens the loss. Check the recipe cost first, then re-price, re-cut, or remove.',
  },
  uncosted: {
    title: 'NO RECIPE COST',
    axis: 'PROFIT UNKNOWN',
    rule: 'These sold, but have no recipe cost in the system, so their profit cannot be measured or scored. Add the recipe so next month can place them.',
  },
};

// A dish whose recipe exists but could not be priced needs fixing, not adding.
const UNCOSTED_RULE_MIXED =
  'These sold, but have no recipe cost in the system, so their profit cannot be measured or scored. Add or fix the recipe so next month can place them.';

const QUADRANT_HEAD = (
  <thead>
    <tr>
      <th>#</th>
      <th>Item Name</th>
      <th>Qty Sold</th>
      <th>Sales</th>
      <th>Profit / Plate</th>
      <th>Gross Profit</th>
      <th>Margin %</th>
    </tr>
  </thead>
);

/** One page's worth of a quadrant block. No row caps: the rest continues on the next page. */
function QuadrantSegment({ block, segment }) {
  const rows = block.rows.slice(segment.from, segment.to);
  return (
    <div className="seom-quadrant" data-testid={`quadrant-${block.key}`}>
      <h3>
        <span>
          {block.meta.title}
          {segment.continued ? ' (CONTINUED)' : ''}
        </span>
        <em>{block.meta.axis}</em>
      </h3>
      {!segment.continued && (
        <p className="seom-quadrant-rule">
          <strong>Owner Action Rule:</strong> {block.meta.rule}
        </p>
      )}
      {block.rows.length === 0 ? (
        <p className="seom-empty">No dish fell into this group this period.</p>
      ) : (
        <table className="seom-table">
          {QUADRANT_HEAD}
          <tbody>
            {rows.map((item, n) => (
              <tr key={item.menu_item_id}>
                <td>{segment.from + n + 1}</td>
                <td><DishName item={item} /></td>
                <td>{qty(item.total_sold)}</td>
                <td>{inr(item.sales_revenue)}</td>
                <td>{inrOrGap(item.profit_per_plate)}</td>
                <td>{inrOrGap(item.gross_profit)}</td>
                <td>{pct(item.margin_percentage)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function QuadrantPage({ descriptor, page, pages }) {
  const { title, lede, continued, segments, blocks, flow, flowPage, id } = descriptor;
  return (
    <SectionPage
      section="03 — MENU INSIGHT MATRIX"
      title={title}
      lede={lede}
      continued={continued}
      page={page}
      pages={pages}
      testid={id}
      flow={flow}
      flowPage={flowPage}
    >
      {segments.map((segment) => (
        <QuadrantSegment key={`${segment.key}-${segment.from}`} block={blocks[segment.key]} segment={segment} />
      ))}
    </SectionPage>
  );
}

// ---------------------------------------------------------------------------
// 04 — CHANNEL LEDGER
// ---------------------------------------------------------------------------
function ChannelLedger({ ledger, footnote, narratives, ties, page, pages }) {
  const rows = (ledger && ledger.rows) || [];
  const all = (ledger && ledger.all) || {};
  const maxSales = Math.max(...rows.map((r) => Number(r.sales) || 0), 0);
  const leakage = (narratives && narratives.leakage_note) || null;

  return (
    <SectionPage
      section="04 — CHANNEL LEDGER"
      title="MULTI-CHANNEL BREAKDOWN"
      lede={`The same dish yields different profit per channel once platform commissions and funded discounts are applied. Direct / QR keeps the full ticket.${
        ties && ties.channels_to_summary ? ' The totals below are the same figures as section 01.' : ''
      }`}
      page={page}
      pages={pages}
      testid="channels"
    >
      {rows.length === 0 ? (
        <p className="seom-empty">No channel recorded sales for this period.</p>
      ) : (
        <table className="seom-table">
          <thead>
            <tr>
              <th>Channel</th>
              <th>Volume</th>
              <th>Sales</th>
              <th>Platform Deductions</th>
              <th>COGS</th>
              <th>Gross Profit</th>
              <th>Avg Margin</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key}>
                <td>{r.label}</td>
                <td>{qty(r.volume)}</td>
                <td>{inr(r.sales)}</td>
                <td>
                  {inr(r.deductions)} ({pct(r.deduction_pct)})
                </td>
                <td>{inr(r.cogs)}</td>
                <td>{inr(r.gross_profit)}</td>
                <td>{pct(r.avg_margin_pct)}</td>
              </tr>
            ))}
            <tr className="seom-total-row">
              <td>ALL CHANNELS</td>
              <td>{qty(all.volume)}</td>
              <td>{inr(all.sales)}</td>
              <td>{inr(all.deductions)}</td>
              <td>{inr(all.cogs)}</td>
              <td>{inr(all.gross_profit)}</td>
              <td>{pct(all.avg_margin_pct)}</td>
            </tr>
          </tbody>
        </table>
      )}

      {rows.length > 0 && (
        <div className="seom-chart">
          <h3>Channel Sales vs Net Realisation</h3>
          {rows.map((r, index) => (
            <React.Fragment key={r.key}>
              <div className="seom-bar-row">
                <span>{r.label} Sales</span>
                <div>
                  <i
                    className={`seom-pattern seom-pattern-${index % 5}`}
                    style={{ width: widthOf(r.sales, maxSales) }}
                  />
                </div>
                <strong>{inr(r.sales)}</strong>
              </div>
              <div className="seom-bar-row">
                <span>{r.label} Net</span>
                <div>
                  <i className="seom-pattern seom-pattern-4" style={{ width: widthOf(r.net, maxSales) }} />
                </div>
                <strong>{inr(r.net)}</strong>
              </div>
            </React.Fragment>
          ))}
        </div>
      )}

      {leakage && (
        <ReadBox heading="EXECUTIVE CHANNEL LEAKAGE NOTE" read={leakage.text} quiet={!leakage.fired} />
      )}

      <Footnote footnote={footnote} />
    </SectionPage>
  );
}

// ---------------------------------------------------------------------------
// 05 — MENU PERFORMANCE BY CATEGORY
// ---------------------------------------------------------------------------
/**
 * One table per page, two row types: a category header row, then its dishes indented under
 * it. A category's dishes may continue onto the next page; its header row never ends one.
 *
 * Categories rank by the gross profit they EARN, and dishes inside them do too. Ranking by
 * revenue instead — which an earlier layout did — floats a high-turnover, thin-margin
 * category above one that quietly earns more, which is the opposite of what this page is
 * for. `Share %` stays a revenue measure: a dish's slice of its own category's sales.
 */
const CATEGORY_HEAD = (
  <thead>
    <tr>
      <th>#</th>
      <th>Category / Item</th>
      <th>Qty Sold</th>
      <th>Sales</th>
      <th>Share %</th>
      <th>COGS</th>
      <th>Gross Profit</th>
      <th>Margin %</th>
    </tr>
  </thead>
);

/** Flatten categories into ledger rows: a header row, then its dishes. */
function categoryRows(categories, startIndex) {
  return categories.flatMap((c, index) => [
    { type: 'cat', key: `cat-${c.category_name}`, rank: startIndex + index + 1, category: c },
    ...(c.items || []).map((item, n) => ({ type: 'item', key: `item-${item.menu_item_id}`, rank: n + 1, item })),
  ]);
}

function LedgerRow({ row }) {
  if (row.type === 'cat') {
    const c = row.category;
    return (
      <tr className="seom-cat-row">
        <td>{row.rank}</td>
        <td>{String(c.category_name).toUpperCase()}</td>
        <td>{qty(c.total_sold)}</td>
        <td>{inr(c.sales_revenue)}</td>
        <td>100%</td>
        <td>{inr(c.cogs)}</td>
        <td>{inr(c.gross_profit)}</td>
        <td>{pct(c.margin_pct)}</td>
      </tr>
    );
  }
  const item = row.item;
  return (
    <tr className={`seom-item-row${item.is_costed ? '' : ' seom-row-gap'}`}>
      <td>{row.rank}</td>
      <td><DishName item={item} /></td>
      <td>{qty(item.total_sold)}</td>
      <td>{inr(item.sales_revenue)}</td>
      <td>{pct(item.share_pct)}</td>
      <td>{inrOrGap(item.total_cogs)}</td>
      <td>{inrOrGap(item.gross_profit)}</td>
      <td>{pct(item.margin_percentage)}</td>
    </tr>
  );
}

/**
 * In-house lines that are not a dish: bill lines not on the menu, whatever the bill totals
 * hold beyond their items, and wastage not tied to a dish. The Sales Report counts all
 * three, so they sit in the ledger as their own group — counted in the totals row beneath
 * them — rather than as a gap.
 */
/**
 * The non-dish lines worth a row: a line that rounds to ₹0 in every column (paise of
 * rounding between the bill totals and their items) stays in the totals but is not drawn.
 */
const visibleAdjustments = (lines) =>
  (lines || []).filter((a) =>
    [a.sales_revenue, a.cogs, a.gross_profit].some((v) => Math.abs(Number(v) || 0) >= 0.5)
  );

function AdjustmentRows({ lines: allLines }) {
  const lines = visibleAdjustments(allLines);
  if (!lines.length) return null;
  const isWastage = (a) => a.key === 'other_wastage';
  const sales = lines.reduce((s, a) => s + (Number(a.sales_revenue) || 0), 0);
  const cogs = lines.reduce((s, a) => s + (Number(a.cogs) || 0), 0);
  const profit = lines.reduce((s, a) => s + (Number(a.gross_profit) || 0), 0);
  const label = (a) =>
    a.key === 'non_menu' && a.line_count > 0
      ? `${a.label} (${qty(a.line_count)} line${a.line_count === 1 ? '' : 's'})`
      : a.label;
  return (
    <>
      <tr className="seom-cat-row seom-adjust-row" data-testid="ledger-adjustments">
        <td />
        <td>{lines.some(isWastage) ? 'NOT A DISH' : 'NOT ON THE MENU'}</td>
        <td>—</td>
        <td>{inr(sales)}</td>
        <td>—</td>
        <td>{inr(cogs)}</td>
        <td>{inr(profit)}</td>
        <td>{sales > 0 ? pct((profit / sales) * 100) : '—'}</td>
      </tr>
      {lines.map((a, n) => (
        <tr key={a.key} className="seom-item-row">
          <td>{n + 1}</td>
          <td>{label(a)}</td>
          <td>—</td>
          <td>{isWastage(a) ? '—' : inr(a.sales_revenue)}</td>
          <td>—</td>
          <td>{a.held_out > 0 || isWastage(a) ? '—' : inr(a.cogs)}</td>
          <td>{a.held_out > 0 ? '—' : inr(a.gross_profit)}</td>
          <td>{a.held_out > 0 || !(a.sales_revenue > 0) ? '—' : pct((a.gross_profit / a.sales_revenue) * 100)}</td>
        </tr>
      ))}
    </>
  );
}

function TotalsRow({ totals }) {
  return (
    <tr className="seom-total-row">
      <td colSpan={2}>TOTALS · {totals.category_count} CATEGORIES</td>
      <td>{qty(totals.qty)}</td>
      <td>{inr(totals.sales)}</td>
      <td />
      <td>{inr(totals.cogs)}</td>
      <td>{inr(totals.gross_profit)}</td>
      <td>{pct(totals.blended_margin_pct)}</td>
    </tr>
  );
}

function CategoryPage({ descriptor, categories, narratives, footnote, page, pages }) {
  const { title, lede, continued, rows, from, to, last, tail, flow, flowPage, id } = descriptor;
  const slice = rows.slice(from, to);
  return (
    <SectionPage
      section="05 — MENU PERFORMANCE BY CATEGORY"
      title={title}
      lede={lede}
      continued={continued}
      page={page}
      pages={pages}
      testid={id}
      flow={flow}
      flowPage={flowPage}
    >
      {rows.length === 0 ? (
        <p className="seom-empty">No category sales for this period.</p>
      ) : (
        (slice.length > 0 || (last && tail.totals)) && (
          <table className="seom-table seom-ledger">
            {CATEGORY_HEAD}
            <tbody>
              {slice.map((row) => (
                <LedgerRow key={row.key} row={row} />
              ))}
              {last && tail.totals && <AdjustmentRows lines={categories.adjustments} />}
              {last && tail.totals && <TotalsRow totals={categories.totals} />}
            </tbody>
          </table>
        )
      )}
      {last && tail.read && <ReadBox heading="CATEGORY READ" read={narratives && narratives.category_read} />}
      {last && tail.footnote && <Footnote footnote={footnote} />}
    </SectionPage>
  );
}

// ---------------------------------------------------------------------------
// 06 — CATEGORY VISUALISER
// ---------------------------------------------------------------------------
/**
 * Sales share against profit share, both bars on ONE scale so the pair can be read against
 * each other at a glance. A category whose profit bar overhangs its sales bar returns more
 * than its size — the cheapest place to grow income.
 */
function GrowNext({ visualiser, narratives, page, pages }) {
  const rows = visualiser || [];
  const max = Math.max(
    ...rows.flatMap((c) => [Number(c.sales_share_pct) || 0, Number(c.profit_share_pct) || 0]),
    0
  );

  return (
    <SectionPage
      section="06 — CATEGORY VISUALISER"
      title="WHERE TO GROW NEXT"
      lede="Sales share against profit share for the top five categories, both on one scale, so you can see which categories earn more than their size."
      page={page}
      pages={pages}
      testid="grow"
    >
      <div className="seom-vis">
        {rows.length === 0 && <p className="seom-empty">No category sales for this period.</p>}
        {rows.map((c) => (
          <div className="seom-vis-group" key={c.label}>
            <div className="seom-vis-row">
              <span>SALES SHARE</span>
              <div>
                <i className="seom-vis-fill seom-vis-sales" style={{ width: widthOf(c.sales_share_pct, max) }} />
              </div>
              <strong>{pct(c.sales_share_pct)}</strong>
            </div>
            <p className="seom-vis-label">{String(c.label).toUpperCase()}</p>
            <div className="seom-vis-row">
              <span>PROFIT SHARE</span>
              <div>
                <i className="seom-vis-fill seom-vis-profit" style={{ width: widthOf(c.profit_share_pct, max) }} />
              </div>
              <strong>{pct(c.profit_share_pct)}</strong>
            </div>
          </div>
        ))}
      </div>

      <ReadBox heading="GROWTH READ" read={narratives && narratives.growth_read} />
    </SectionPage>
  );
}

// ---------------------------------------------------------------------------
// 07 — OWNER'S BRIEF
// ---------------------------------------------------------------------------
/** The month in a few sentences, then the moves, biggest money first. All text is server-built. */
function OwnerBrief({ brief, page, pages }) {
  const bottomLine = brief.bottom_line || [];
  const moves = brief.moves || [];
  const count = bottomLine.length;
  const inWords = COUNT_WORDS[count] || String(count);

  return (
    <SectionPage
      section="07 — OWNER'S BRIEF"
      title="WHAT TO DO NEXT MONTH"
      lede={`The month in ${inWords} sentence${count === 1 ? '' : 's'}, then the moves that matter most, ranked by the money they are worth.`}
      page={page}
      pages={pages}
      testid="brief"
    >
      {count > 0 && (
        <div className="seom-bottomline">
          <h3>OWNER'S BOTTOM LINE</h3>
          <p>{bottomLine.join(' ')}</p>
        </div>
      )}

      <h3 className="seom-subhead">NEXT MONTH'S MOVES · BIGGEST MONEY FIRST</h3>
      <ul className="seom-moves">
        {moves.map((m) => (
          <li key={m.key}>
            <span className="seom-move-label">{String(m.label).toUpperCase()}</span>
            <strong>{m.headline}</strong> {m.text}
          </li>
        ))}
      </ul>

      {brief.note && <p className="seom-brief-note">{brief.note}</p>}
    </SectionPage>
  );
}

// ---------------------------------------------------------------------------
// Cover
// ---------------------------------------------------------------------------
function Cover({ monthLabel, pages }) {
  return (
    <section className="seom-page seom-cover" data-seom-section="cover">
      <h1>SALES</h1>
      <p>End of Month Report</p>
      <span>{monthLabel}</span>
      <Footer page={1} pages={pages} />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Page list
// ---------------------------------------------------------------------------
const NO_SHAVES = {};
// Measuring stops after this many corrections, whatever the pages look like.
const MAX_MEASURE_PASSES = 60;
// Below this page width the layout is a phone view, not an A4 sheet.
const MIN_MEASURE_WIDTH = 700;

/** Flow pages get `${flow}` for their first page and `${flow}-2`, `-3`… after it. */
const flowPageId = (flow, index) => (index === 0 ? flow : `${flow}-${index + 1}`);

/**
 * Every page this month's data produces, in reading order.
 *
 * `shaves` holds measured overflow, in pixels, per flow page (`${flow}:${index}`); it only
 * ever shrinks a page's budget, so rows move forward and never back.
 *
 * The low-volume pages are omitted when nothing lands on them — no Puzzles or
 * Underperformers (the engine ran its margin-only fallback), nothing sold below cost and
 * nothing without a recipe cost. The smaller-categories pages are likewise absent on a menu
 * of three categories or fewer, which closes on the biggest-categories pages instead.
 */
function buildPageList(data, shaves) {
  if (!data) return [];
  const budgetFor = (flow) => (index) => PAGE_BUDGET - (shaves[`${flow}:${index}`] || 0);
  const narratives = data.narratives || {};
  const intros = narratives.quadrant_intros || {};
  const quadrants = data.quadrants || {};
  const footnote = data.footnote || { show: false };
  const footnoteText = footnote.show ? footnote.text : null;
  const pages = [{ id: 'cover', kind: 'cover' }, { id: 'summary', kind: 'summary' }, { id: 'top-profit', kind: 'top-profit' }];

  // ---- 03: quadrant flows ------------------------------------------------
  const uncostedRows = ((data.data_quality && data.data_quality.items) || []).map((i) => ({
    menu_item_id: i.menu_item_id,
    item_name: i.item_name,
    total_sold: i.total_sold,
    sales_revenue: i.sales_revenue,
    profit_per_plate: null,
    gross_profit: null,
    margin_percentage: null,
  }));
  const allNoRecipe = ((data.data_quality && data.data_quality.items) || []).every(
    (i) => !i.reason || i.reason === 'no_recipe'
  );
  const blockOf = (key, rows, meta = QUADRANTS[key]) => ({
    key,
    meta,
    rows: rows || [],
    head: H.blockGap + H.blockTitle + ruleHeight(meta.rule) + H.quadTableHead,
    contHead: H.blockGap + H.blockTitle + H.quadTableHead,
    rowHeight: H.quadRow,
    emptyHeight: H.empty,
  });

  const quadrantFlow = (flow, title, lede, blockList) => {
    const blocks = Object.fromEntries(blockList.map((b) => [b.key, b]));
    paginateBlocks(blockList, {
      firstHeader: H.header + ledeHeight(lede),
      contHeader: H.header,
      budget: budgetFor(flow),
    }).forEach((segments, index) => {
      pages.push({
        id: flowPageId(flow, index),
        kind: 'quadrants',
        flow,
        flowPage: index,
        title,
        lede,
        continued: index > 0,
        segments,
        blocks,
      });
    });
  };

  quadrantFlow('high-volume', 'YOUR HIGH-VOLUME DISHES', intros.high_volume, [
    blockOf('stars', quadrants.stars),
    blockOf('workhorses', quadrants.workhorses),
  ]);

  const lossMakers = quadrants.loss_makers || [];
  const hasLowVolume =
    (quadrants.puzzles || []).length > 0 ||
    (quadrants.underperformers || []).length > 0 ||
    lossMakers.length > 0 ||
    uncostedRows.length > 0;
  if (hasLowVolume) {
    const low = [blockOf('puzzles', quadrants.puzzles), blockOf('underperformers', quadrants.underperformers)];
    // The loss list and the no-recipe list are not extra quadrants — they sit outside the
    // 2x2 entirely, so a dish sold below cost can never be dressed up as a healthy box. Each
    // appears only when something is actually in it.
    if (lossMakers.length) low.push(blockOf('loss_makers', lossMakers));
    if (uncostedRows.length) {
      low.push(
        blockOf('uncosted', uncostedRows, allNoRecipe ? QUADRANTS.uncosted : { ...QUADRANTS.uncosted, rule: UNCOSTED_RULE_MIXED })
      );
    }
    quadrantFlow('low-volume', 'YOUR LOW-VOLUME DISHES', intros.low_volume, low);
  }

  pages.push({ id: 'channels', kind: 'channels' });

  // ---- 05: category flows ------------------------------------------------
  const categories = data.categories || { biggest: [], smaller: [], totals: {}, top3_gp_share_pct: 0 };
  const ties = data.ties || {};
  const biggest = categories.biggest || [];
  const smaller = categories.smaller || [];
  const singlePage = smaller.length === 0;
  const readText = joinRead(narratives.category_read);
  const lossLine = sentenceCase(narratives.loss_line || '');

  const topCount = biggest.length;
  const these =
    topCount === 1 ? 'This category earns' : `These ${COUNT_WORDS[topCount] || topCount} categories earn`;
  const biggestLede =
    'Every dish sold this month, grouped under its category. Categories are ranked by gross profit, and so are the dishes inside each one. ' +
    'This is the complete item ledger, so you can see what each dish earned and whether a category rests on one hero product. ' +
    `${these} ${pct(categories.top3_gp_share_pct, 0)} of all gross profit. ` +
    "Share % is a dish's slice of its own category's sales.";
  const smallerLede =
    `The remaining ${smaller.length === 1 ? 'category' : `${smaller.length} categories`} on the same basis, closing with the whole-menu totals` +
    `${ties.categories_to_summary ? ', which match section 01 exactly' : ''}.${lossLine ? ` ${lossLine}` : ''}`;

  const categoryFlow = (flow, title, lede, rows, tail) => {
    const adjustmentCount = visibleAdjustments(categories.adjustments).length;
    const tailHeight =
      (tail.totals ? H.totalsRow + (adjustmentCount ? (adjustmentCount + 1) * H.catRow : 0) : 0) +
      (tail.read ? readHeight(readText) : 0) +
      (tail.footnote ? footnoteHeight(footnoteText) : 0);
    paginateRows(rows, {
      firstHeader: H.header + ledeHeight(lede),
      contHeader: H.header,
      head: H.ledgerTableHead,
      rowHeight: (row) => (row.type === 'cat' ? H.catRow : H.itemRow),
      tail: tailHeight,
      budget: budgetFor(flow),
      keepWithNext: (row) => row.type === 'cat',
    }).forEach((slice, index) => {
      pages.push({
        id: flowPageId(flow, index),
        kind: 'categories',
        flow,
        flowPage: index,
        title,
        lede,
        continued: index > 0,
        rows,
        tail,
        ...slice,
      });
    });
  };

  categoryFlow(
    'categories-biggest',
    'YOUR BIGGEST CATEGORIES',
    biggestLede,
    categoryRows(biggest, 0),
    // A menu of three categories or fewer closes here: totals, read and footnote together.
    singlePage ? { totals: biggest.length > 0, read: true, footnote: true } : { footnote: true }
  );
  if (!singlePage) {
    categoryFlow('categories-smaller', 'YOUR SMALLER CATEGORIES', smallerLede, categoryRows(smaller, biggest.length), {
      totals: true,
      read: true,
    });
  }

  pages.push({ id: 'grow', kind: 'grow' });
  if (narratives.owner_brief) pages.push({ id: 'brief', kind: 'brief' });
  return pages;
}

// ---------------------------------------------------------------------------
// Period picker helpers
// ---------------------------------------------------------------------------
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const pad2 = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const parseDay = (d) => {
  if (!d) return null;
  const dateObj = new Date(`${String(d).slice(0, 10)}T00:00:00`);
  return isNaN(dateObj.getTime()) ? null : dateObj;
};

const fmtNice = (d) => {
  const dateObj = parseDay(d);
  if (!dateObj) return '';
  return `${pad2(dateObj.getDate())}/${pad2(dateObj.getMonth() + 1)}/${dateObj.getFullYear()}`;
};

function buildMonthLabel(startDate, endDate) {
  const s = parseDay(startDate);
  const e = parseDay(endDate);
  if (!s && !e) return '';
  if (s && e) {
    return `${fmtNice(startDate)} – ${fmtNice(endDate)}`;
  }
  if (e) {
    return fmtNice(endDate);
  }
  return fmtNice(startDate);
}

/** Read the business onboarding date cached at login (Businesses.created_at). */
function readOnboardingDate() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    const raw = u.businessCreatedAt || u.business_created_at || u.createdAt || u.created_at;
    return raw ? String(raw).slice(0, 10) : '';
  } catch (_) {
    return '';
  }
}

/**
 * One selectable period per month, from the onboarding month through the current month.
 *   - onboarding month : onboarding date -> that month's last day
 *   - middle months    : 1st -> last day
 *   - current month    : 1st -> today  (the live "to date" period)
 * A leading "all" option covers the whole span (onboarding -> latest closing stock); the months
 * follow it newest first.
 */
function buildPeriods(onboardingDate) {
  const options = [{ key: 'all', label: 'Full period · onboarding → last closing', start: '', end: '', isAll: true }];
  if (!onboardingDate) return options;
  const startD = parseDay(onboardingDate);
  if (!startD) return options;

  const today = new Date();
  const endY = today.getFullYear();
  const endM = today.getMonth();
  const months = [];
  let y = startD.getFullYear();
  let m = startD.getMonth();

  while (y < endY || (y === endY && m <= endM)) {
    const isFirst = y === startD.getFullYear() && m === startD.getMonth();
    const isCurrent = y === endY && m === endM;
    const lastDay = new Date(y, m + 1, 0).getDate();
    const start = isFirst ? onboardingDate : `${y}-${pad2(m + 1)}-01`;
    const end = isCurrent ? ymd(today) : `${y}-${pad2(m + 1)}-${pad2(lastDay)}`;
    months.push({
      key: `${y}-${pad2(m + 1)}`,
      label: isCurrent ? `${MONTH_NAMES[m]} ${y} · to date` : `${MONTH_NAMES[m]} ${y}`,
      start,
      end,
      isCurrent,
    });
    m += 1;
    if (m > 11) { m = 0; y += 1; }
  }
  // Newest first — this month to date, then last month, and back — as the Expense and
  // Leakage EOM pickers already list them.
  return [...options, ...months.reverse()];
}

// ---------------------------------------------------------------------------
// A3 — "Confirm these prices"
//
// The month will not sign off while part of its cost is priced from an old purchase bill.
// What lacks a price is an INGREDIENT, not a dish — nobody can count a Fish N Chips — so the
// card lists the ingredients, each with the price the report estimated with and the dishes
// it sits in, and the owner confirms or corrects it. The confirmed price goes into the
// shared cost engine (POST /api/reports/sales-eom/confirm-prices), so this report and the
// app's Sales Report re-price with the same figure and keep printing the same profit.
// ---------------------------------------------------------------------------
const DISHES_SHOWN = 3;

const fmtBillDay = (d) => {
  const dateObj = parseDay(d);
  return dateObj ? `${dateObj.getDate()} ${MONTH_NAMES[dateObj.getMonth()]} ${dateObj.getFullYear()}` : null;
};

/** How far a typed price sits from the bill it replaces. A typo guard, not a rule. */
const priceDrift = (typed, suggested) => {
  const t = Number(typed);
  const s = Number(suggested);
  if (!(t > 0) || !(s > 0)) return null;
  const ratio = t / s;
  return ratio >= 3 || ratio <= 1 / 3 ? ratio : null;
};

const isBlank = (value) => String(value ?? '').trim() === '';

function EstimatedPriceRow({ ingredient, value, onChange, disabled }) {
  const [expanded, setExpanded] = useState(false);
  const dishes = ingredient.dishes || [];
  const shown = expanded ? dishes : dishes.slice(0, DISHES_SHOWN);
  const hidden = dishes.length - shown.length;
  const billDay = fmtBillDay(ingredient.priced_on);
  const invalid = !isBlank(value) && !(Number(value) > 0);
  const drift = invalid ? null : priceDrift(value, ingredient.display_price);
  const inputId = `seom-price-${ingredient.item_id}`;

  return (
    <div className="seom-price-row">
      <div className="seom-price-main">
        <label className="seom-price-name" htmlFor={inputId}>{ingredient.name}</label>
        {dishes.length > 0 && (
          <div className="seom-price-dishes">
            ({shown.join(', ')}
            {hidden > 0 && (
              <>
                {', '}
                <button type="button" className="seom-price-more" onClick={() => setExpanded(true)}>
                  +{hidden} more
                </button>
              </>
            )}
            )
          </div>
        )}
        <div className="seom-price-meta">
          {billDay ? `Last bill ${billDay}` : 'Last bill price'}
          {Number(ingredient.estimated_cogs) > 0 && ` · ${inr(ingredient.estimated_cogs)} of this month’s cost`}
        </div>
        {invalid && <div className="seom-price-warn">Enter a price above ₹0, or clear the box to skip it.</div>}
        {drift && (
          <div className="seom-price-warn">
            Check this — {drift >= 1 ? `${drift.toFixed(1)}×` : `1/${(1 / drift).toFixed(1)} of`} the last bill.
          </div>
        )}
      </div>
      <div className={`seom-price-input${invalid ? ' is-invalid' : ''}`}>
        <span aria-hidden="true">₹</span>
        <input
          id={inputId}
          type="number"
          inputMode="decimal"
          min="0"
          step="any"
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
        <span className="seom-price-unit">/ {ingredient.display_unit}</span>
      </div>
    </div>
  );
}

/**
 * The popup shown from the moment Confirm is pressed until the report is back.
 *
 *   saving    the prices are being written
 *   saved     written; the month is re-costing with them — Undo is offered here
 *   undoing   the undo request is in flight
 *   reopening undone; the price list is being reloaded
 *   failed    the undo did not go through — retry or close
 */
function RecalcPopup({ recalc, periodLabel, onUndo, onClose }) {
  const { phase, count, error } = recalc;
  const n = `${count} price${count === 1 ? '' : 's'}`;
  const copy = {
    saving: [`Saving ${n}…`, 'One moment.'],
    saved: [`${n} saved`, `Recalculating ${periodLabel} with your prices. This takes about half a minute — the report opens by itself.`],
    undoing: ['Undoing…', `Removing the ${n} you just confirmed.`],
    reopening: ['Undone', `The ${n} were removed. Reopening the price list for ${periodLabel}…`],
    failed: ['Couldn’t undo', error || 'Please try again.'],
  }[phase] || ['Working…', ''];
  const busy = phase !== 'failed';

  return (
    <div className="seom-gate">
      <div
        className="seom-gate-card seom-gate-card--loading"
        role="dialog"
        aria-modal="true"
        aria-labelledby="seom-recalc-title"
        aria-busy={busy}
      >
        {busy ? <div className="seom-spinner" aria-hidden="true" /> : <div className="seom-gate-icon">!</div>}
        <h3 id="seom-recalc-title">{copy[0]}</h3>
        <p role="status">{copy[1]}</p>
        {phase === 'saved' && (
          <div className="seom-gate-actions">
            <button type="button" className="seom-gate-link" onClick={onUndo}>
              Confirmed by mistake? Undo
            </button>
          </div>
        )}
        {phase === 'failed' && (
          <div className="seom-gate-actions">
            <button type="button" className="seom-gate-primary" onClick={onUndo}>Try undo again</button>
            <button type="button" className="seom-gate-link" onClick={onClose}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
}

function EstimatedPriceConfirm({ gateInfo, periodLabel, onConfirmed, onCountInstead, onSignOff, onClose }) {
  const est = gateInfo.estimated || {};
  const ingredients = est.ingredients || [];
  const period = gateInfo.period || {};
  // Pre-filled with the price the report is already using, so confirming as shown leaves the
  // cost exactly where it is and only removes the "estimate" label.
  const [values, setValues] = useState(() => Object.fromEntries(
    ingredients.map((i) => [i.item_id, i.display_price === null || i.display_price === undefined ? '' : String(i.display_price)])
  ));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const filled = ingredients.filter((i) => !isBlank(values[i.item_id]));
  const anyInvalid = filled.some((i) => !(Number(values[i.item_id]) > 0));

  const submit = async () => {
    if (filled.length === 0 || anyInvalid || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/reports/sales-eom/confirm-prices', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          startDate: period.startDate,
          endDate: period.endDate,
          prices: filled.map((i) => ({ item_id: i.item_id, price: Number(values[i.item_id]) })),
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) throw new Error(json.error || 'The prices could not be saved. Please try again.');
      onConfirmed(Number(json.confirmed) || filled.length, json.confirmation_ids || []);
    } catch (err) {
      setSaveError(err.message);
      setSaving(false);
    }
  };

  // While the prices post, the card gives way to the progress popup. Its state (the typed
  // prices) lives on in this component, so a failed save comes back to the card untouched.
  if (saving) {
    return <RecalcPopup recalc={{ phase: 'saving', count: filled.length }} periodLabel={periodLabel} />;
  }

  return (
    <div className="seom-gate">
      <div className="seom-gate-card seom-gate-card--prices" role="dialog" aria-modal="true" aria-labelledby="seom-price-title">
        <button type="button" className="seom-gate-close" aria-label="Cancel" onClick={onClose}>
          ✕
        </button>
        <div className="seom-gate-icon">≈</div>
        <h3 id="seom-price-title">Confirm these prices</h3>
        <p>
          ₹{Number(est.amount || 0).toLocaleString('en-IN')} of the cost for {periodLabel} ({est.share_pct}% of
          COGS) is priced from earlier purchase bills, because these ingredients weren’t in a closing
          stock count. Each box shows the last bill price — confirm it, or correct it. This report and
          the app’s Sales Report will both use the price you confirm.
        </p>
        {gateInfo.override_refused && <p className="seom-price-error">{gateInfo.message}</p>}
        <div className="seom-price-head">
          <span>Ingredient <em>(dishes it’s used in)</em></span>
          <span>Price</span>
        </div>
        <div className="seom-price-list">
          {ingredients.map((ingredient) => (
            <EstimatedPriceRow
              key={ingredient.item_id}
              ingredient={ingredient}
              value={values[ingredient.item_id] ?? ''}
              disabled={saving}
              onChange={(v) => setValues((prev) => ({ ...prev, [ingredient.item_id]: v }))}
            />
          ))}
        </div>
        {saveError && <p className="seom-price-error" role="alert">{saveError}</p>}
        <div className="seom-gate-actions">
          <button
            type="button"
            className="seom-gate-primary"
            onClick={submit}
            disabled={saving || filled.length === 0 || anyInvalid}
          >
            {saving
              ? 'Saving…'
              : `Confirm ${filled.length} price${filled.length === 1 ? '' : 's'}`}
          </button>
          <div className="seom-price-alt">
            <button type="button" className="seom-gate-link" onClick={onCountInstead} disabled={saving}>
              Give closing stock instead
            </button>
            {/* The escape hatch. Deliberately secondary and deliberately worded as a
                signature: pressing it writes the owner's name and the estimated share to
                the audit log, and the report comes back stamped "not certified". */}
            <button
              type="button"
              className="seom-gate-link"
              onClick={onSignOff}
              disabled={saving}
              title="Records your name and the estimated share in the audit log. The report will be marked as not certified."
            >
              Sign off anyway
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
export default function SalesEom({ goTo }) {
  const businessId = localStorage.getItem('businessId');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gated, setGated] = useState(false);
  const [exporting, setExporting] = useState(false);
  const rootRef = useRef(null);

  // Onboarding date drives the month list. Seeded from the login cache and corrected once
  // from the first server response if the cache was empty.
  const [onboardingDate, setOnboardingDate] = useState(readOnboardingDate);
  const [selectedKey, setSelectedKey] = useState('all');
  // Lets the owner dismiss the gate card while staying on the page to pick another period.
  const [gateDismissed, setGateDismissed] = useState(false);
  // A3. The server refuses to certify a period whose cost leans on estimated prices, and
  // sends back WHICH dishes and how much. Held whole so the card can name them: "this
  // period contains estimates" tells an owner nothing they can act on.
  const [gateInfo, setGateInfo] = useState(null);
  // Set only by the owner pressing "Sign off anyway". Never sticky across periods — an
  // override is a decision about one month, and carrying it forward would silently sign
  // off every month after it.
  const [overrideEstimates, setOverrideEstimates] = useState(false);
  // Bumped after the owner confirms prices, to re-ask the server: the confirmed prices
  // re-cost the month, and the gate clears on its own once nothing is estimated.
  const [reloadTick, setReloadTick] = useState(0);
  // The after-Confirm sequence — { phase, count, ids, error }, see RecalcPopup. Mirrored in a
  // ref because the report fetch is a closure and must see the phase as it is NOW: a fetch
  // that finishes while an undo is in flight must not close the popup under it.
  const [recalc, setRecalcState] = useState(null);
  const recalcRef = useRef(null);
  const setRecalc = (next) => {
    recalcRef.current = next;
    setRecalcState(next);
  };
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const periods = useMemo(() => buildPeriods(onboardingDate), [onboardingDate]);
  const selected = useMemo(
    () => periods.find((p) => p.key === selectedKey) || periods[0],
    [periods, selectedKey]
  );

  useEffect(() => {
    if (!businessId) return;

    let cancelled = false;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        setGated(false);
        setGateDismissed(false);
        setGateInfo(null);
        const token = localStorage.getItem('token');
        const qs = new URLSearchParams({ businessId });
        // "all" omits the dates so the server uses its carry-forward default; a month sends
        // that month's window and the server gates it unless it holds a closing stock count.
        if (selected && !selected.isAll && selected.start && selected.end) {
          qs.set('startDate', selected.start);
          qs.set('endDate', selected.end);
        }
        if (overrideEstimates) qs.set('overrideEstimates', '1');
        const res = await fetch(`/api/reports/sales-eom?${qs.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to fetch EOM data');
        const json = await res.json();
        if (cancelled) return;

        const respOnboarding = json.period?.onboardingDate || json.data?.period?.onboardingDate;
        if (respOnboarding && !onboardingDate) setOnboardingDate(String(respOnboarding).slice(0, 10));

        if (json.gated) {
          setGated(true);
          setGateInfo(json);
          setData(null);
          return;
        }
        if (json.success) {
          // ── No client-side overlay any more ──────────────────────────────
          //
          // This used to fire a SECOND request to /api/reports/summary here and overwrite
          // summary.total_cogs / net_profit / gross_profit with the answer, so that the
          // month-end page would agree with the Sales Report card. Three things were wrong
          // with doing it here:
          //
          //   - it was silent when it failed. The whole block sat in a try/catch that only
          //     logged, so any hiccup on that second request left the page showing the
          //     EOM's own, different numbers with nothing on screen to say so.
          //   - it patched COGS and profit but never REVENUE, which is where the two
          //     screens were furthest apart (Rs 4.70L for one business's July).
          //   - it mutated the payload after the server's identity checks had passed, so
          //     the boxes could end up contradicting the channel table below them.
          //
          // The server now computes the headline from the same function that produces the
          // Sales Report card (routes/salesEomReport.js -> computeSalesSummary), against
          // this report's own window, and checks the reconciliation as identity I7 before
          // responding. There is nothing left to patch.
          setData(json.data);
        } else {
          throw new Error(json.error || 'Unknown error');
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) {
          setLoading(false);
          const phase = recalcRef.current && recalcRef.current.phase;
          if (phase === 'saved' || phase === 'reopening') setRecalc(null);
        }
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, selectedKey, onboardingDate, overrideEstimates, reloadTick]);

  // Switching period drops any sign-off the owner gave for the previous one.
  useEffect(() => {
    setOverrideEstimates(false);
    setRecalc(null);
  }, [selectedKey]);

  // "Confirmed by mistake? Undo" — reverts exactly the rows that Confirm just wrote, then
  // reloads, which brings the price list back.
  const undoConfirmation = async () => {
    const current = recalcRef.current;
    if (!current || current.phase === 'undoing' || !(current.ids || []).length) return;
    setRecalc({ ...current, phase: 'undoing', error: null });
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/reports/sales-eom/revert-prices', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ confirmation_ids: current.ids }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) throw new Error(json.error || 'The prices could not be undone. Please try again.');
      setRecalc({ ...current, phase: 'reopening', error: null });
      setReloadTick((t) => t + 1);
    } catch (err) {
      setRecalc({ ...current, phase: 'failed', error: err.message });
    }
  };

  const startDate = data?.period?.startDate || selected?.start;
  const endDate = data?.period?.endDate || selected?.end;
  const monthLabel = useMemo(() => buildMonthLabel(startDate, endDate), [startDate, endDate]);

  // ---- Page breaking ------------------------------------------------------
  // Estimated breaks first (sales_eom.pages.js), then measured: any page whose content runs
  // into the footer zone has its budget shrunk by the overflow and the list is rebuilt, so
  // rows move forward until every page fits one A4 sheet. Corrections belong to the data
  // they were measured against and are dropped when the data or the page width changes.
  const [shaveState, setShaveState] = useState({ data: null, map: {} });
  const shaves = shaveState.data === data ? shaveState.map : NO_SHAVES;
  const passes = useRef({ data: null, count: 0 });
  const [measureTick, setMeasureTick] = useState(0);

  const pageList = useMemo(() => buildPageList(data, shaves), [data, shaves]);

  useEffect(() => {
    // Web fonts change line heights; measure again once they are in.
    let alive = true;
    if (typeof document !== 'undefined' && document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => alive && setMeasureTick((n) => n + 1));
    }
    let timer = null;
    const onResize = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        // A new width means new line wraps: start the corrections over.
        setShaveState({ data: null, map: {} });
        passes.current = { data: null, count: 0 };
        setMeasureTick((n) => n + 1);
      }, 200);
    };
    window.addEventListener('resize', onResize);
    return () => {
      alive = false;
      clearTimeout(timer);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || !data) return;
    if (passes.current.data !== data) passes.current = { data, count: 0 };
    if (passes.current.count >= MAX_MEASURE_PASSES) return;

    for (const el of root.querySelectorAll('[data-seom-flow]')) {
      const width = el.offsetWidth;
      // No layout (jsdom) or a phone-width page: the estimates stand.
      if (!width || width < MIN_MEASURE_WIDTH) return;
      const limit = width * A4_RATIO - FOOTER_RESERVE;
      const top = el.getBoundingClientRect().top;
      let bottom = 0;
      for (const child of el.children) {
        if (child.classList.contains('seom-footer')) continue;
        bottom = Math.max(bottom, child.getBoundingClientRect().bottom - top);
      }
      if (bottom > limit) {
        const key = `${el.dataset.seomFlow}:${el.dataset.seomFlowPage}`;
        const overflow = Math.ceil(bottom - limit) + 4;
        passes.current.count += 1;
        setShaveState((s) => {
          const map = s.data === data ? s.map : {};
          return { data, map: { ...map, [key]: (map[key] || 0) + overflow } };
        });
        return;
      }
    }
  }, [data, pageList, measureTick]);

  const handleExport = async () => {
    if (!rootRef.current) return;
    setExporting(true);
    try {
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      // Every page that rendered, in order — the page count follows the month's data.
      const sectionSelectors = [...rootRef.current.querySelectorAll('[data-seom-section]')].map(
        (el) => `[data-seom-section="${el.dataset.seomSection}"]`
      );
      await exportSectionsToPDF({
        container: rootRef.current,
        sectionSelectors,
        title: 'Sales EOM Report',
        periodLabel: monthLabel,
        fullPageMode: true,
        forcePageBreakPerSection: true,
        hideSelectorsDuringCapture: ['.seom-toolbar'],
      });
    } catch (err) {
      setError(`PDF export failed: ${err.message}`);
    } finally {
      setExporting(false);
    }
  };

  if (!businessId) return <div style={{ padding: '2rem' }}>Please select a business.</div>;

  const toolbar = (
    <div className="seom-toolbar">
      <div className="seom-toolbar-left">
        <label className="seom-period-label">Period</label>
        <div className="seom-dropdown-container" ref={dropdownRef}>
          <button
            type="button"
            className="seom-dropdown-trigger"
            onClick={() => setDropdownOpen(!dropdownOpen)}
          >
            {selected?.label || 'Select Period'}
            <span className="seom-dropdown-arrow">▾</span>
          </button>
          {dropdownOpen && (
            <div className="seom-dropdown-menu">
              {periods.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  className={`seom-dropdown-item${selectedKey === p.key ? ' seom-dropdown-item-active' : ''}`}
                  onClick={() => {
                    setSelectedKey(p.key);
                    setDropdownOpen(false);
                  }}
                >
                  {p.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <button type="button" className="seom-export-btn" onClick={handleExport} disabled={exporting || !data}>
        {exporting ? 'Preparing PDF…' : '⬇ Export PDF'}
      </button>
    </div>
  );

  let body;
  const recalcPhase = recalc ? recalc.phase : null;
  if (recalcPhase && (loading || recalcPhase === 'undoing' || recalcPhase === 'failed')) {
    body = (
      <RecalcPopup
        recalc={recalc}
        periodLabel={selected ? selected.label : 'this period'}
        onUndo={undoConfirmation}
        onClose={() => setRecalc(null)}
      />
    );
  } else if (loading) {
    body = (
      <div style={{ padding: '3rem', textAlign: 'center' }}>
        <h2>Loading Sales End-of-Month Report…</h2>
      </div>
    );
  } else if (error) {
    body = <div style={{ padding: '2rem', color: 'red' }}>Error: {error}</div>;
  } else if (gated && gateDismissed && gateInfo && gateInfo.gate_reason === 'estimated_cogs') {
    body = (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#6b6b6b' }}>
        Some ingredient prices for {selected ? selected.label : 'this period'} aren’t confirmed yet, so
        the month can’t be signed off.{' '}
        <button type="button" className="seom-gate-link" onClick={() => setGateDismissed(false)}>
          Confirm prices
        </button>
      </div>
    );
  } else if (gated && gateDismissed) {
    body = (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#6b6b6b' }}>
        Closing stock isn’t submitted for {selected ? selected.label : 'this period'}. Pick another
        period from the dropdown above, or give closing stock to view this report.
      </div>
    );
  } else if (gated && gateInfo && gateInfo.gate_reason === 'estimated_cogs'
    && (gateInfo.estimated?.ingredients || []).length > 0) {
    // A3. A different refusal from "no closing stock", and it needs a different card. The
    // period IS reconciled — it simply contains money priced from an old purchase price,
    // and a signed-off month must not rest on that. The card names the INGREDIENTS behind it
    // with the price each was estimated at, for the owner to confirm or correct.
    body = (
      <EstimatedPriceConfirm
        gateInfo={gateInfo}
        periodLabel={selected ? selected.label : 'this period'}
        onConfirmed={(count, ids) => {
          setRecalc({ phase: 'saved', count: count || 1, ids: ids || [], error: null });
          setReloadTick((t) => t + 1);
        }}
        onCountInstead={() => goTo && goTo('closing-stock')}
        onSignOff={() => setOverrideEstimates(true)}
        onClose={() => setGateDismissed(true)}
      />
    );
  } else if (gated && gateInfo && gateInfo.gate_reason === 'estimated_cogs') {
    // Fallback when the server could not list the ingredients: the dishes, as before.
    const est = gateInfo.estimated || {};
    const list = est.item_list || [];
    body = (
      <div className="seom-gate">
        <div className="seom-gate-card">
          <button
            type="button"
            className="seom-gate-close"
            aria-label="Cancel"
            onClick={() => setGateDismissed(true)}
          >
            ✕
          </button>
          <div className="seom-gate-icon">≈</div>
          <h3>This period isn’t measured yet</h3>
          <p>
            ₹{Number(est.amount || 0).toLocaleString('en-IN')} of the cost for{' '}
            {selected ? selected.label : 'this period'} ({est.share_pct}% of COGS) was estimated
            from earlier purchase prices rather than counted. A month you sign off shouldn’t rest
            on a guess.
          </p>
          {gateInfo.override_refused && (
            <p style={{ marginTop: 8, color: '#b45309', fontWeight: 600 }}>{gateInfo.message}</p>
          )}
          {list.length > 0 && (
            <div style={{ marginTop: 12, textAlign: 'left', maxHeight: 200, overflowY: 'auto' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#4b5563', marginBottom: 6 }}>
                Dishes affected:
              </div>
              {list.map((i) => (
                <div key={i.menu_item_id} style={{ fontSize: 12, color: '#4b5563', display: 'flex', justifyContent: 'space-between', gap: 12, padding: '2px 0' }}>
                  <span>{i.item_name}</span>
                  <span style={{ whiteSpace: 'nowrap' }}>₹{Number(i.estimated_cogs || 0).toLocaleString('en-IN')}</span>
                </div>
              ))}
            </div>
          )}
          <div className="seom-gate-actions">
            <button
              type="button"
              className="seom-gate-primary"
              onClick={() => goTo && goTo('closing-stock')}
            >
              Give Closing Stock
            </button>
            {/* The escape hatch. Deliberately secondary and deliberately worded as a
                signature: pressing it writes the owner's name and the estimated share to
                the audit log, and the report comes back stamped "not certified". */}
            <button
              type="button"
              onClick={() => setOverrideEstimates(true)}
              style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 14px', fontSize: 13, color: '#6b7280', cursor: 'pointer', marginLeft: 8 }}
              title="Records your name and the estimated share in the audit log. The report will be marked as not certified."
            >
              Sign off anyway
            </button>
          </div>
        </div>
      </div>
    );
  } else if (gated) {
    body = (
      <div className="seom-gate">
        <div className="seom-gate-card">
          <button
            type="button"
            className="seom-gate-close"
            aria-label="Cancel"
            onClick={() => setGateDismissed(true)}
          >
            ✕
          </button>
          <div className="seom-gate-icon">🔒</div>
          <h3>Closing stock required</h3>
          <p>
            {selected && selected.isCurrent
              ? 'This is the live current period. It can only be viewed once you submit closing stock for it.'
              : `The Sales report for ${selected ? selected.label : 'this period'} can only be viewed after closing stock is submitted for it.`}
          </p>
          <p style={{ marginTop: 8 }}>
            Give closing stock to view this — or pick another period from the dropdown above.
          </p>
          <div className="seom-gate-actions">
            <button
              type="button"
              className="seom-gate-primary"
              onClick={() => goTo && goTo('closing-stock')}
            >
              Give Closing Stock
            </button>
          </div>
        </div>
      </div>
    );
  } else if (!data) {
    body = null;
  }

  if (body !== undefined) {
    return (
      <div style={{ backgroundColor: '#f0ede6', minHeight: '100vh', paddingBottom: '2rem' }}>
        {toolbar}
        {body}
      </div>
    );
  }

  const summary = data.summary || {};
  const categories = data.categories || { biggest: [], smaller: [], totals: {}, top3_gp_share_pct: 0 };
  const narratives = data.narratives || {};
  const footnote = data.footnote || { show: false };
  const ties = data.ties || {};
  const pages = pageList.length;

  const renderPage = (p, index) => {
    const page = index + 1;
    switch (p.kind) {
      case 'cover':
        return <Cover key={p.id} monthLabel={monthLabel} pages={pages} />;
      case 'summary':
        return (
          <SalesSummary
            key={p.id}
            summary={summary}
            footnote={footnote}
            narratives={narratives}
            ties={ties}
            page={page}
            pages={pages}
          />
        );
      case 'top-profit':
        return <TopProfit key={p.id} topProfit={data.top_profit} narratives={narratives} page={page} pages={pages} />;
      case 'quadrants':
        return <QuadrantPage key={p.id} descriptor={p} page={page} pages={pages} />;
      case 'channels':
        return (
          <ChannelLedger
            key={p.id}
            ledger={data.channel_ledger}
            footnote={footnote}
            narratives={narratives}
            ties={ties}
            page={page}
            pages={pages}
          />
        );
      case 'categories':
        return (
          <CategoryPage
            key={p.id}
            descriptor={p}
            categories={categories}
            narratives={narratives}
            footnote={footnote}
            page={page}
            pages={pages}
          />
        );
      case 'grow':
        return <GrowNext key={p.id} visualiser={data.visualiser} narratives={narratives} page={page} pages={pages} />;
      case 'brief':
        return <OwnerBrief key={p.id} brief={narratives.owner_brief} page={page} pages={pages} />;
      default:
        return null;
    }
  };

  return (
    <div style={{ backgroundColor: '#f0ede6', minHeight: '100vh', paddingBottom: '2rem' }}>
      {toolbar}
      <main className="seom-root" ref={rootRef} style={{ marginTop: '24px' }}>
        {/* A3. An overridden month must never be readable as a counted one. Deliberately
            INSIDE the exported root (rootRef is what the PDF capture walks), so the stamp
            travels with the file — which outlives the click that produced it and is read
            by people who never saw the gate. */}
        {data.certification && data.certification.certified === false && (
          <div style={{ margin: '0 auto 16px', maxWidth: 900, padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: 13, color: '#92400e', lineHeight: 1.45 }}>
            <strong>Not certified.</strong> ₹{Number(data.certification.estimated?.amount || 0).toLocaleString('en-IN')} of
            this period’s cost ({data.certification.estimated?.share_pct}%) is estimated from earlier
            purchase prices, not counted. Signed off under override; the decision is recorded in the audit log.
          </div>
        )}
        {pageList.map(renderPage)}
      </main>
    </div>
  );
}
